/**
 * The one table.
 *
 * Twelve plain tables and four dense ones, none sortable, none paginated, and
 * five of them "spreadsheet forms" with an input in every cell. This is the
 * table every list screen renders: dense by default, sortable by clicking a
 * header, a sticky header, and a row that opens something looks like it does.
 *
 * TanStack Table does the sorting and the column model; the markup is a real
 * `<table>`, which is what the Playwright harness measures — see the
 * `display:table` assertion in `e2e/views.spec.ts` for why that matters.
 */

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';

export type { ColumnDef };

export function DataTable<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  rowClass,
  initialSort,
  minWidth,
  empty,
  caption,
}: {
  data: readonly T[];
  columns: ColumnDef<T, unknown>[];
  getRowId?: (row: T) => string;
  /** Makes rows a target. Keyboard-reachable: each row is focusable and Enter opens it. */
  onRowClick?: (row: T) => void;
  /** Per-row emphasis — `row-fault`, `row-muted`. */
  rowClass?: (row: T) => string | undefined;
  initialSort?: SortingState;
  /** Scroll inside the card past this width rather than shrinking columns to nothing. */
  minWidth?: number;
  /** Rendered in place of the body when there are no rows. */
  empty?: ReactNode;
  caption?: string;
}): React.JSX.Element {
  const [sorting, setSorting] = useState<SortingState>(initialSort ?? []);

  const table = useReactTable({
    data: data as T[],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getRowId !== undefined ? { getRowId } : {}),
  });

  const rows = table.getRowModel().rows;

  const open = (row: Row<T>): void => onRowClick?.(row.original);

  return (
    <div className="table-wrap">
      <table
        className={onRowClick !== undefined ? 'data dense clickable' : 'data dense'}
        style={minWidth !== undefined ? { minWidth } : undefined}
      >
        {caption !== undefined && <caption className="sr-only">{caption}</caption>}
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => {
                const sortable = header.column.getCanSort();
                const dir = header.column.getIsSorted();
                const align = header.column.columnDef.meta?.align;
                return (
                  <th
                    key={header.id}
                    className={align === 'right' ? 'num' : undefined}
                    aria-sort={
                      dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined
                    }
                    style={
                      header.column.columnDef.size !== undefined && header.column.columnDef.size !== 150
                        ? { width: header.column.columnDef.size }
                        : undefined
                    }
                  >
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        type="button"
                        className="th-sort"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span className="th-sort-icon" aria-hidden="true">
                          {dir === 'asc' ? (
                            <ArrowUp size={12} />
                          ) : dir === 'desc' ? (
                            <ArrowDown size={12} />
                          ) : (
                            <ChevronsUpDown size={12} />
                          )}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length === 0 && empty !== undefined ? (
            <tr>
              <td colSpan={columns.length} className="table-empty">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const extra = rowClass?.(row.original);
              const clickable = onRowClick !== undefined;
              return (
                <tr
                  key={row.id}
                  className={extra}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => open(row) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' && e.target === e.currentTarget) open(row);
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef.meta?.align === 'right' ? 'num' : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    align?: 'left' | 'right';
  }
}
